import { AdditionalEquipment } from "services/api/catalog/cvh/types";
import { EQUIPMENT_GROUPS } from "services/api/sales/listings/consts";

// Fuzzy mapping CVH `AdditionalEquipment[].name` (slobodni tekst na hrvatskom)
// → marketplace equipment chip code (`abs`, `esp`, ...).
//
// Algoritam:
//   1. Normalizira ime (lowercase, strip dijakritike, strip non-alphanum).
//   2. Provjerava poklapa li se s ručno definiranim keywordima per chip.
//   3. Vraća `code` chipa ili `null` (chip se ne označava).

const normalize = (s: string): string =>
  s
    .toLowerCase()
    // Strip Croatian/Latin dijakritike: č→c, ć→c, š→s, ž→z, đ→d
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Mapa chip code → lista ključnih riječi/fraza koje označavaju match.
// Sve fraze su već normalizirane (lowercase, bez dijakritika).
const KEYWORDS: Record<string, string[]> = {
  // Sigurnost
  abs: ["abs"],
  esp: ["esp", "elektronski program stabilnosti"],
  edc: ["edc"],
  ets: ["ets"],
  asr: ["asr"],
  asd: ["asd"],
  seatbelt_pretensioners: ["pojas", "pirotehni", "samozateza"],
  isofix: ["isofix"],
  adaptive_cruise: ["adaptiv", "tempomat", "acc"],
  lane_departure_warning: ["napustanje trake", "lane departure"],
  lane_keeping: ["zadrzav", "lane keep"],
  rear_collision_protection: ["straznj", "naleta"],
  side_collision_protection: ["bocn", "naleta"],

  // Dodatna
  aluminium_wheels: ["aluminij", "naplaci", "alu felg"],
  sports_suspension: ["sport", "podvozje"],
  "4x4": ["4x4", "awd", "all wheel"],
  third_brake_light: ["3 stop", "trec stop"],
  front_fog_lights: ["magl", "prednj"],
  tpms: ["tpms", "pritis", "pneumatik"],
  xenon_lights: ["ksenon", "xenon"],
  bi_xenon_lights: ["bi ksenon", "bi xenon"],
  led_lights: ["led"],
  adaptive_lights: ["prilagodljiv", "adaptiv svjet"],
  light_sensor: ["senzor", "svjetlo"],
  rain_sensor: ["senzor", "kis"],
  navigation: ["navigacij", "gps"],
  navigation_tv: ["navigacij tv"],
  trip_computer: ["putno racun", "racunalo"],
  cargo_net: ["mreza", "prtljaz"],
  roof_rack: ["krovn nosac"],
  tow_hitch: ["kuka", "vuc"],
  tinted_windows: ["zatamnj", "tonir"],
  leather_steering_wheel: ["upravljac", "koz"],
  sunroof: ["krovni prozor", "sunroof"],
  roof_box: ["krovna kut"],
  head_up_display: ["head up", "hud"],
  start_stop: ["start stop"],
  disabled_adapted: ["invalid"],

  // Anti-theft
  anti_theft_alarm: ["alarm"],
  engine_immobilizer: ["blokada motora", "immobilizer"],
  central_locking: ["centralno zaklj"],
  central_locking_remote: ["daljinskim"],
  multilock: ["multilock"],

  // Udobnost
  el_seat_adjustment: ["el podesav", "elektri", "sjedal"],
  split_rear_bench: ["djeljiva", "klup", "straznj"],
  mobile_prep: ["pripr", "mobiln"],
  handsfree: ["handsfree", "hands free"],
  el_mirror_adjustment: ["el podesav", "ogledal"],
  el_mirror_folding: ["el sklap", "ogledal"],
  cd_changer: ["cd izmj"],
  el_front_windows: ["el podiz", "prednj", "stakal"],
  el_rear_windows: ["el podiz", "straznj", "stakal"],
  height_adjustable_steering: ["upravljac", "podesiv", "visin"],
  power_steering: ["servo", "upravljac"],
  heated_mirrors: ["grijanj", "ogledal"],
  parking_sensors: ["parkirni senzor", "parking sensor"],
  front_camera: ["prednj", "kamera"],
  rear_camera: ["straznj", "kamera"],
  cruise_control: ["tempomat", "cruise"],
  central_armrest: ["sredisnj", "naslon"],
  leather_seats: ["koz", "sjedal"],
  sport_seats: ["sport", "sjedal"],
  heated_seats: ["grijanj", "sjedal"],
  steering_wheel_controls: ["komande", "upravljac"],
  android_auto: ["android auto"],
  apple_carplay: ["carplay", "apple"],
};

// Match imena na chip code. Vraća prvi chip čija sva ključna riječ se nađe u
// normaliziranom imenu (sve fraze chipa moraju biti present — to filtrira
// pogreške tipa "el podiz" + "prednj" idu samo na `el_front_windows`).
const matchName = (name: string): string | null => {
  const norm = normalize(name);
  for (const [code, keywords] of Object.entries(KEYWORDS)) {
    const allMatch = keywords.every((kw) => norm.includes(normalize(kw)));
    if (allMatch) return code;
  }
  return null;
};

/**
 * Rezultat mapiranja: čipovi koje treba označiti i nazivi koje nijedan čip ne pokriva. Nemapirano
 * se vraća da se korisniku može pokazati što nije preneseno — inače bi tiho nestalo.
 */
export interface EquipmentMatchResult {
  selected: Record<string, boolean>;
  unmatched: string[];
}

/** Sve što ima naziv može se mapirati — i CVH katalog i oprema unesena na vozilu. */
interface NamedEquipment {
  name: string | null;
}

/**
 * Mapira popis opreme na čipove oglasa. Uzima sve stavke koje imaju naziv: CVH lista po definiciji
 * opisuje opremu prisutnu za taj JOM, a oprema vozila je ono što je netko unio za taj konkretni auto.
 */
export const matchEquipment = (equipment: NamedEquipment[]): EquipmentMatchResult => {
  const selected: Record<string, boolean> = {};
  const unmatched: string[] = [];

  // Set svih važećih chip codes — sigurnosna provjera (typo guard u
  // KEYWORDS-u koji bi mapao na nepostojeći code).
  const validCodes = new Set<string>(
    EQUIPMENT_GROUPS.flatMap((g) => g.items.map((i) => i.code))
  );

  for (const item of equipment) {
    if (!item.name) continue;

    const code = matchName(item.name);

    if (code && validCodes.has(code)) {
      selected[code] = true;
      continue;
    }

    if (!unmatched.includes(item.name)) unmatched.push(item.name);
  }

  return { selected, unmatched };
};

/** Zadržano radi čitljivosti poziva za CVH katalog; nemapirano ondje nikoga ne zanima. */
export const matchCvhEquipment = (
  cvhEquipment: AdditionalEquipment[]
): Record<string, boolean> => matchEquipment(cvhEquipment).selected;
